import {
  CdkDrag,
  CdkDragDrop,
  copyArrayItem,
  moveItemInArray,
  transferArrayItem,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { includes, isEmpty, startsWith } from 'lodash';
import {
  ReportTemplateField,
  ReportTemplateFieldCategory,
  ReportTemplateFieldDefinition,
  ReportTemplateFieldStyle,
  ReportTemplateFieldType,
} from 'src/dto/report-template';
import { TemplateCanvasActionType } from '../template-canvas-action-type.enum';
import { NgClass, AsyncPipe } from '@angular/common';
import { IconV2Module, ChipV2Module, FileUploadV2Module } from '@schenck/rotec-components';
import { TranslatePipe } from '@ngx-translate/core';
import { SafeAsyncPipe } from '../../../../../pipes/safe-async.pipe';

@Component({
  selector: 'rc-template-canvas-item',
  templateUrl: './template-canvas-item.component.html',
  styleUrls: ['./template-canvas-item.component.less'],
  imports: [
    CdkDropList,
    CdkDrag,
    NgClass,
    IconV2Module,
    ChipV2Module,
    AsyncPipe,
    TranslatePipe,
    FileUploadV2Module,
    SafeAsyncPipe
],
})
export class TemplateCanvasItemComponent implements OnInit {
  @Input('row') rowIdx: number;
  @Input('col') colIdx: number;
  @Input() item: ReportTemplateField;
  @Input() allowedFieldCategories: ReportTemplateFieldCategory[];
  @Output() itemChange: EventEmitter<[TemplateCanvasActionType, ReportTemplateField]>;

  ReportTemplateFieldType = ReportTemplateFieldType;
  items: ReportTemplateField[];

  constructor() {
    this.itemChange = new EventEmitter<[TemplateCanvasActionType, ReportTemplateField]>();
  }

  ngOnInit() {
    this.items = this.item ? [this.item] : [];
  }

  canDrop(allowedFieldCategories: ReportTemplateFieldCategory[]) {
    return (item: CdkDrag<ReportTemplateFieldDefinition>) => {
      return (
        isEmpty(allowedFieldCategories) || includes(allowedFieldCategories, item.data.category)
      );
    };
  }

  onDrop(event: CdkDragDrop<any>) {
    const fieldDefinition = event.item.data as ReportTemplateFieldDefinition;
    if (!fieldDefinition) {
      return;
    }

    const isMoveInsideSameContainer = event.previousContainer === event.container;
    const isMoveBetweenCanvasContainers =
      !isMoveInsideSameContainer &&
      !startsWith(event.previousContainer.id, 'toolbox') &&
      !startsWith(event.container.id, 'toolbox');

    if (isMoveInsideSameContainer) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else if (isMoveBetweenCanvasContainers) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.item = {
      name: fieldDefinition.name,
      type: fieldDefinition.type,
      position: { item1: this.rowIdx, item2: this.colIdx },
      value: fieldDefinition.value,
      style: fieldDefinition.style,
      category: fieldDefinition.category,
    };

    // Notify subscribers
    this.itemChange.emit([TemplateCanvasActionType.Add, this.item]);
  }

  removeItem() {
    // Notify subscribers
    this.itemChange.emit([TemplateCanvasActionType.Remove, this.item]);
    this.item = null;
  }

  preventDrop(): boolean {
    return false;
  }

  isBold(item: ReportTemplateField): boolean {
    return this.hasStyle(item, ReportTemplateFieldStyle.Bold);
  }

  private hasStyle(item: ReportTemplateField, style: ReportTemplateFieldStyle): boolean {
    if (!item || !item.style) {
      return false;
    }

    if (style === ReportTemplateFieldStyle.None) {
      return false;
    }

    if (style === ReportTemplateFieldStyle.All) {
      return true;
    }

    return (item.style & style) === style;
  }
}
